import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router';
import { api, listFrom } from '../api';
import type { Decision, ResearchResult, Responsibility } from '../types';
import { cx } from '../utils';
import { CommandPalette, type CommandItem } from './command-palette';
import { EvidenceProvider } from './evidence';
import { Icon, type IconName } from './icons';

const navigation: Array<{ label: string; path: string; icon: IconName; section?: boolean }> = [
  { label: 'Mission Control', path: '/', icon: 'mission' },
  { label: 'Briefing', path: '/briefing', icon: 'briefing' },
  { label: 'Roadmap', path: '/roadmap', icon: 'roadmap' },
  { label: 'Scenarios', path: '/scenarios', icon: 'scenario' },
  { label: 'Responsibilities', path: '/responsibilities', icon: 'tasks', section: true },
  { label: 'Decisions', path: '/decisions', icon: 'decision' },
  { label: 'Life Modules', path: '/modules', icon: 'modules' },
  { label: 'Research', path: '/research', icon: 'research', section: true },
  { label: 'Sources', path: '/sources', icon: 'sources' },
  { label: 'Profile & Intake', path: '/profile', icon: 'profile', section: true },
  { label: 'Settings & Export', path: '/settings/access', icon: 'settings' },
];

export function AppShell(): React.JSX.Element {
  const location = useLocation();
  const responsibilities = useQuery({
    queryKey: ['responsibilities', 'shell'],
    queryFn: () => api<unknown>('/responsibilities?limit=100'),
  });
  const decisions = useQuery({
    queryKey: ['decisions', 'shell'],
    queryFn: () => api<unknown>('/decisions?limit=100'),
  });
  const research = useQuery({
    queryKey: ['research', 'shell'],
    queryFn: () => api<unknown>('/research/search?limit=500'),
    staleTime: 300_000,
  });
  const commandItems = useMemo<CommandItem[]>(() => {
    const base = navigation.map((item) => ({
      id: `route:${item.path}`,
      title: item.label,
      path: item.path,
      kind: 'Route' as const,
      icon: item.icon,
    }));
    const taskItems = listFrom<Responsibility>(responsibilities.data, ['responsibilities']).map(
      (item) => ({
        id: `responsibility:${item.id}`,
        title: item.title,
        ...((item.nextAction ?? item.next_action)
          ? { subtitle: item.nextAction ?? item.next_action ?? '' }
          : {}),
        path: `/responsibilities?focus=${item.id}`,
        kind: 'Task' as const,
        icon: 'tasks' as const,
      }),
    );
    const decisionItems = listFrom<Decision>(decisions.data, ['decisions']).map((item) => ({
      id: `decision:${item.id}`,
      title: item.title,
      subtitle: item.status,
      path: `/decisions/${item.id}`,
      kind: 'Decision' as const,
      icon: 'decision' as const,
    }));
    const evidenceItems = listFrom<ResearchResult>(research.data, ['results']).map((item) => {
      const kind = (
        item.type ??
        item.kind ??
        (item.claim_id ? 'claim' : item.source_id ? 'source' : 'module')
      ).toLowerCase();
      const recordId =
        item.id ?? item.claim_id ?? item.source_id ?? item.slug ?? item.module_id ?? '';
      const id = `${kind}:${recordId}`;
      const title = item.title ?? item.safe_render ?? recordId;
      if (kind.includes('source'))
        return {
          id,
          title,
          ...(item.jurisdiction ? { subtitle: item.jurisdiction } : {}),
          path: `/sources?focus=${encodeURIComponent(recordId)}`,
          kind: 'Source' as const,
          icon: 'sources' as const,
        };
      if (kind.includes('claim'))
        return {
          id,
          title,
          ...(item.jurisdiction ? { subtitle: item.jurisdiction } : {}),
          path: `/research/claims/${encodeURIComponent(recordId)}`,
          kind: 'Claim' as const,
          icon: 'research' as const,
        };
      if (kind.includes('recommend'))
        return {
          id,
          title,
          path: `/research/recommendations/${encodeURIComponent(recordId)}`,
          kind: 'Recommendation' as const,
          icon: 'research' as const,
        };
      return {
        id,
        title,
        ...(item.summary ? { subtitle: item.summary } : {}),
        path: `/modules/${encodeURIComponent(item.slug ?? recordId)}`,
        kind: 'Module' as const,
        icon: 'modules' as const,
      };
    });
    return [...base, ...taskItems, ...decisionItems, ...evidenceItems];
  }, [responsibilities.data, decisions.data, research.data]);

  const activeLabel =
    navigation.find((item) =>
      item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path),
    )?.label ?? 'Akshay’s Atlas';
  const dueCount = listFrom<Responsibility>(responsibilities.data, ['responsibilities']).filter(
    (item) => ['next', 'in_progress'].includes(item.status),
  ).length;

  return (
    <EvidenceProvider>
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <div className="desktop-app">
        <aside className="sidebar">
          <Link to="/" className="brand">
            <span className="brand__mark">
              <Icon name="atlas" size={22} />
            </span>
            <span>
              <strong>Akshay’s Atlas</strong>
              <small>Private owner cockpit</small>
            </span>
          </Link>
          <nav aria-label="Primary navigation">
            {navigation.map((item) => (
              <NavLink
                end={item.path === '/'}
                key={item.path}
                className={({ isActive }) =>
                  cx('nav-link', item.section && 'nav-link--section', isActive && 'is-active')
                }
                to={item.path}
              >
                <Icon name={item.icon} />
                <span>{item.label}</span>
                {item.label === 'Responsibilities' && dueCount ? (
                  <span className="nav-count">{Math.min(dueCount, 99)}</span>
                ) : null}
              </NavLink>
            ))}
          </nav>
          <div className="sidebar__footer">
            <div className="privacy-status">
              <span className="privacy-status__dot" />
              <div>
                <strong>Owner-only workspace</strong>
                <small>Encrypted sensitive values</small>
              </div>
            </div>
            <Link to="/settings/export" className="sidebar-export">
              <Icon name="download" />
              Export & backup
            </Link>
          </div>
        </aside>
        <header className="topbar">
          <div className="topbar__location">
            <span>{activeLabel}</span>
            <Icon name="chevron" size={13} />
            <span className="muted">Akshay</span>
          </div>
          <div className="topbar__actions">
            <button
              className="search-trigger"
              onClick={() =>
                window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }))
              }
            >
              <Icon name="search" />
              <span>Search atlas</span>
              <kbd>⌘ K</kbd>
            </button>
            <Link
              to="/responsibilities?filter=waiting"
              className="topbar-icon"
              aria-label="Waiting follow-ups"
            >
              <Icon name="bell" />
              {dueCount ? <span /> : null}
            </Link>
            <Link to="/profile" className="avatar" aria-label="Open profile">
              AJ
            </Link>
          </div>
        </header>
        <main className="workspace" id="main-content">
          <Outlet />
        </main>
      </div>
      <CommandPalette items={commandItems} />
      <UnsupportedWidth authenticated />
    </EvidenceProvider>
  );
}

export function UnsupportedWidth({
  authenticated = false,
  children,
}: {
  authenticated?: boolean;
  children?: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="unsupported-width" role="region" aria-label="Unsupported screen width">
      <div className="unsupported-width__card">
        <span className="brand__mark">
          <Icon name="atlas" size={24} />
        </span>
        <p className="eyebrow">Desktop workspace required</p>
        <h1>Akshay’s Atlas needs a wider canvas</h1>
        <p>
          The analytical workspace is supported at 1180 px and above. Use a larger browser window to
          keep evidence, timelines and decision context visible together.
        </p>
        {children}
        {authenticated ? (
          <div className="unsupported-actions">
            <a className="button button--primary" href="/api/v1/export.json" download>
              <Icon name="download" size={16} />
              Export JSON
            </a>
            <a className="button button--default" href="/settings/export">
              Export options
            </a>
          </div>
        ) : null}
        <small>Mobile optimization is intentionally outside V1.</small>
      </div>
    </div>
  );
}
